import { useState, useEffect, useCallback } from 'react';
import { productService, productGroupService, brandService, unitService } from '../services/erpApi';
import { productCacheEvents } from '../utils/productCacheEvents';
import {
  putAllProducts,
  getAllProducts,
  upsertProduct,
  deleteProduct,
  getMeta,
  setMeta,
  putAllGroups,
  getAllGroups,
  putAllBrands,
  getAllBrands,
  putAllUnits,
  getAllUnits,
  clearAllErpCaches,
  clearGroups,
  clearBrands,
  clearUnits,
  filterProductsLocal,
  isBrowserOnline,
  type ErpCachedProduct,
  type MetaKey,
} from '../services/erpProductCacheDb';

/**
 * Interfaces para os dados do ERP
 */
export interface ErpProduct {
  id: number;
  sku?: string;
  barcode?: string;
  name: string;
  description?: string;
  cost_price?: number;
  price: number;
  stock_quantity: number;
  min_stock?: number;
  max_stock?: number;
  active: boolean;
  group_id?: number;
  brand_id?: number;
  unit_id?: number;
  brand_name?: string;
  group_name?: string;
  unit_name?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface ErpProductGroup {
  id: number;
  name: string;
  code?: string;
  description?: string;
  parent_id?: number;
  active: boolean;
}

export interface ErpBrand {
  id: number;
  name: string;
  code?: string;
  active: boolean;
}

export interface ErpUnit {
  id: number;
  name: string;
  code?: string;
  active: boolean;
}

/**
 * TTL controla apenas revalidação de rede — o catálogo offline permanece no IDB.
 */
const CACHE_TTL = {
  products: 10 * 60 * 1000,
  groups: 60 * 60 * 1000,
  brands: 60 * 60 * 1000,
  units: 60 * 60 * 1000,
} as const;

/** Estado em memória (sessão) — compartilhado entre instâncias do hook. */
let memoryProducts: ErpProduct[] | null = null;
let memoryProductsSyncedAt = 0;
let memoryGroups: ErpProductGroup[] | null = null;
let memoryGroupsSyncedAt = 0;
let memoryBrands: ErpBrand[] | null = null;
let memoryBrandsSyncedAt = 0;
let memoryUnits: ErpUnit[] | null = null;
let memoryUnitsSyncedAt = 0;

let productsRevalidating = false;
let groupsRevalidating = false;
let brandsRevalidating = false;
let unitsRevalidating = false;

type StatusListener = () => void;
const statusListeners = new Set<StatusListener>();

function emitStatus(): void {
  statusListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function isFresh(syncedAt: number, ttl: number): boolean {
  if (!syncedAt) return false;
  return Date.now() - syncedAt < ttl;
}

function catalogMissingPhotoCount(products: ErpProduct[]): boolean {
  return products.some((product) => product.image_count === undefined || product.image_count === null);
}

/**
 * Hook para gerenciar cache de produtos do ERP (IndexedDB + memória + SWR).
 */
export const useErpProductCache = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [revalidating, setRevalidating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState<boolean>(!isBrowserOnline());
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(
    memoryProductsSyncedAt || null
  );
  const [isStale, setIsStale] = useState<boolean>(() =>
    memoryProductsSyncedAt
      ? !isFresh(memoryProductsSyncedAt, CACHE_TTL.products)
      : true
  );

  const refreshStatusFlags = useCallback(() => {
    setLastSyncedAt(memoryProductsSyncedAt || null);
    setIsStale(
      !memoryProductsSyncedAt ||
        !isFresh(memoryProductsSyncedAt, CACHE_TTL.products)
    );
    setRevalidating(productsRevalidating);
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setIsOffline(false);
      emitStatus();
    };
    const onOffline = () => {
      setIsOffline(true);
      emitStatus();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    statusListeners.add(refreshStatusFlags);
    refreshStatusFlags();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      statusListeners.delete(refreshStatusFlags);
    };
  }, [refreshStatusFlags]);

  const hydrateProductsFromIdb = useCallback(async (): Promise<ErpProduct[]> => {
    const rows = await getAllProducts();
    const meta = await getMeta('products');
    memoryProducts = rows as ErpProduct[];
    memoryProductsSyncedAt = meta?.syncedAt || 0;
    emitStatus();
    return memoryProducts;
  }, []);

  const fetchProductsFromApi = useCallback(async (): Promise<ErpProduct[]> => {
    const response = await productService.getAll();
    if (response.success && response.data) {
      return response.data as ErpProduct[];
    }
    return [];
  }, []);

  const persistProducts = useCallback(async (products: ErpProduct[]) => {
    await putAllProducts(products as ErpCachedProduct[]);
    memoryProducts = products;
    memoryProductsSyncedAt = Date.now();
    emitStatus();
  }, []);

  const updateProductsInBackground = useCallback(async (): Promise<void> => {
    if (productsRevalidating || !isBrowserOnline()) return;
    productsRevalidating = true;
    emitStatus();
    try {
      const products = await fetchProductsFromApi();
      await persistProducts(products);
    } catch (err) {
      console.warn('[useErpProductCache] Revalidação de produtos falhou:', err);
    } finally {
      productsRevalidating = false;
      emitStatus();
    }
  }, [fetchProductsFromApi, persistProducts]);

  const getProducts = useCallback(async (): Promise<ErpProduct[]> => {
    setLoading(true);
    setError(null);

    try {
      // 1) Memória
      if (memoryProducts) {
        if (
          (!isFresh(memoryProductsSyncedAt, CACHE_TTL.products) ||
            catalogMissingPhotoCount(memoryProducts)) &&
          isBrowserOnline()
        ) {
          void updateProductsInBackground();
        }
        return memoryProducts;
      }

      // 2) IndexedDB
      const fromIdb = await hydrateProductsFromIdb();
      if (fromIdb.length > 0) {
        if (
          (!isFresh(memoryProductsSyncedAt, CACHE_TTL.products) ||
            catalogMissingPhotoCount(fromIdb)) &&
          isBrowserOnline()
        ) {
          void updateProductsInBackground();
        }
        return fromIdb;
      }

      // 3) Rede
      if (!isBrowserOnline()) {
        const msg = 'Sem catálogo em cache. Conecte-se para carregar os produtos.';
        setError(msg);
        throw new Error(msg);
      }

      const products = await fetchProductsFromApi();
      await persistProducts(products);
      return products;
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage =
        anyErr?.response?.data?.message ||
        anyErr?.message ||
        'Erro ao carregar produtos';
      setError(errorMessage);

      if (!memoryProducts) {
        try {
          const fallback = await hydrateProductsFromIdb();
          if (fallback.length > 0) return fallback;
        } catch {
          /* ignore */
        }
      } else {
        return memoryProducts;
      }

      throw err;
    } finally {
      setLoading(false);
      refreshStatusFlags();
    }
  }, [
    hydrateProductsFromIdb,
    fetchProductsFromApi,
    persistProducts,
    updateProductsInBackground,
    refreshStatusFlags,
  ]);

  const searchProductsLocal = useCallback((term: string): ErpProduct[] => {
    const source = memoryProducts || [];
    return filterProductsLocal(source as ErpCachedProduct[], term) as ErpProduct[];
  }, []);

  // ——— Groups / Brands / Units (mesmo padrão) ———

  const loadList = useCallback(
    async <T extends { id: number }>(opts: {
      memoryGet: () => T[] | null;
      memorySet: (items: T[], syncedAt: number) => void;
      memorySyncedAt: () => number;
      ttl: number;
      getIdb: () => Promise<T[]>;
      metaKey: MetaKey;
      fetchApi: () => Promise<T[]>;
      putIdb: (items: T[]) => Promise<void>;
      revalidatingFlag: { get: () => boolean; set: (v: boolean) => void };
    }): Promise<T[]> => {
      const mem = opts.memoryGet();
      if (mem) {
        if (!isFresh(opts.memorySyncedAt(), opts.ttl) && isBrowserOnline()) {
          if (!opts.revalidatingFlag.get()) {
            opts.revalidatingFlag.set(true);
            opts
              .fetchApi()
              .then(async (items) => {
                await opts.putIdb(items);
                opts.memorySet(items, Date.now());
              })
              .catch(() => {})
              .finally(() => opts.revalidatingFlag.set(false));
          }
        }
        return mem;
      }

      const fromIdb = await opts.getIdb();
      const meta = await getMeta(opts.metaKey);
      if (fromIdb.length > 0) {
        opts.memorySet(fromIdb, meta?.syncedAt || 0);
        if (!isFresh(meta?.syncedAt || 0, opts.ttl) && isBrowserOnline()) {
          if (!opts.revalidatingFlag.get()) {
            opts.revalidatingFlag.set(true);
            opts
              .fetchApi()
              .then(async (items) => {
                await opts.putIdb(items);
                opts.memorySet(items, Date.now());
              })
              .catch(() => {})
              .finally(() => opts.revalidatingFlag.set(false));
          }
        }
        return fromIdb;
      }

      if (!isBrowserOnline()) return [];

      const items = await opts.fetchApi();
      await opts.putIdb(items);
      opts.memorySet(items, Date.now());
      return items;
    },
    []
  );

  const getGroups = useCallback(async (): Promise<ErpProductGroup[]> => {
    try {
      return await loadList<ErpProductGroup>({
        memoryGet: () => memoryGroups,
        memorySet: (items, syncedAt) => {
          memoryGroups = items;
          memoryGroupsSyncedAt = syncedAt;
        },
        memorySyncedAt: () => memoryGroupsSyncedAt,
        ttl: CACHE_TTL.groups,
        getIdb: getAllGroups,
        metaKey: 'groups',
        fetchApi: async () => {
          const response = await productGroupService.getAll({ active: true });
          return (response.success && response.data ? response.data : []) as ErpProductGroup[];
        },
        putIdb: putAllGroups,
        revalidatingFlag: {
          get: () => groupsRevalidating,
          set: (v) => {
            groupsRevalidating = v;
          },
        },
      });
    } catch (err) {
      console.error('[useErpProductCache] Erro ao carregar grupos:', err);
      return memoryGroups || [];
    }
  }, [loadList]);

  const getBrands = useCallback(async (): Promise<ErpBrand[]> => {
    try {
      return await loadList<ErpBrand>({
        memoryGet: () => memoryBrands,
        memorySet: (items, syncedAt) => {
          memoryBrands = items;
          memoryBrandsSyncedAt = syncedAt;
        },
        memorySyncedAt: () => memoryBrandsSyncedAt,
        ttl: CACHE_TTL.brands,
        getIdb: getAllBrands,
        metaKey: 'brands',
        fetchApi: async () => {
          const response = await brandService.getAll({ active: true });
          return (response.success && response.data ? response.data : []) as ErpBrand[];
        },
        putIdb: putAllBrands,
        revalidatingFlag: {
          get: () => brandsRevalidating,
          set: (v) => {
            brandsRevalidating = v;
          },
        },
      });
    } catch (err) {
      console.error('[useErpProductCache] Erro ao carregar marcas:', err);
      return memoryBrands || [];
    }
  }, [loadList]);

  const getUnits = useCallback(async (): Promise<ErpUnit[]> => {
    try {
      return await loadList<ErpUnit>({
        memoryGet: () => memoryUnits,
        memorySet: (items, syncedAt) => {
          memoryUnits = items;
          memoryUnitsSyncedAt = syncedAt;
        },
        memorySyncedAt: () => memoryUnitsSyncedAt,
        ttl: CACHE_TTL.units,
        getIdb: getAllUnits,
        metaKey: 'units',
        fetchApi: async () => {
          const response = await unitService.getAll({ active: true });
          return (response.success && response.data ? response.data : []) as ErpUnit[];
        },
        putIdb: putAllUnits,
        revalidatingFlag: {
          get: () => unitsRevalidating,
          set: (v) => {
            unitsRevalidating = v;
          },
        },
      });
    } catch (err) {
      console.error('[useErpProductCache] Erro ao carregar unidades:', err);
      return memoryUnits || [];
    }
  }, [loadList]);

  const updateProductInCache = useCallback((productId: number, updatedProduct: ErpProduct) => {
    if (memoryProducts) {
      memoryProducts = memoryProducts.map((p) =>
        p.id === productId ? { ...p, ...updatedProduct } : p
      );
    }
    void upsertProduct({ id: productId, ...updatedProduct } as ErpCachedProduct);
    emitStatus();
  }, []);

  const addProductToCache = useCallback((newProduct: ErpProduct) => {
    if (memoryProducts) {
      memoryProducts = [...memoryProducts, newProduct];
    } else {
      memoryProducts = [newProduct];
    }
    void upsertProduct(newProduct as ErpCachedProduct);
    emitStatus();
  }, []);

  const removeProductFromCache = useCallback((productId: number) => {
    if (memoryProducts) {
      memoryProducts = memoryProducts.filter((p) => p.id !== productId);
    }
    void deleteProduct(productId);
    emitStatus();
  }, []);

  const updateProductStockInCache = useCallback(
    (productId: number, stockQuantity: number) => {
      if (memoryProducts) {
        memoryProducts = memoryProducts.map((p) =>
          p.id === productId ? { ...p, stock_quantity: stockQuantity } : p
        );
        const found = memoryProducts.find((p) => p.id === productId);
        if (found) void upsertProduct(found as ErpCachedProduct);
      }
      emitStatus();
    },
    []
  );

  const refreshProductInCache = useCallback(
    async (productId: number): Promise<void> => {
      if (!isBrowserOnline()) return;
      try {
        const response = await productService.getById(productId);
        if (response.success && response.data) {
          updateProductInCache(productId, response.data as ErpProduct);
        }
      } catch (err) {
        console.warn(
          `[useErpProductCache] Erro ao atualizar produto ${productId} no cache:`,
          err
        );
      }
    },
    [updateProductInCache]
  );

  /** Marca catálogo como stale e revalida em background (mantém offline). */
  const invalidateProducts = useCallback(() => {
    memoryProductsSyncedAt = 0;
    void setMeta('products', 0, memoryProducts?.length || 0);
    emitStatus();
    if (isBrowserOnline()) void updateProductsInBackground();
  }, [updateProductsInBackground]);

  const invalidateGroups = useCallback(() => {
    memoryGroups = null;
    memoryGroupsSyncedAt = 0;
    void clearGroups();
  }, []);

  const invalidateBrands = useCallback(() => {
    memoryBrands = null;
    memoryBrandsSyncedAt = 0;
    void clearBrands();
  }, []);

  const invalidateUnits = useCallback(() => {
    memoryUnits = null;
    memoryUnitsSyncedAt = 0;
    void clearUnits();
  }, []);

  const clearAllCaches = useCallback(async () => {
    memoryProducts = null;
    memoryProductsSyncedAt = 0;
    memoryGroups = null;
    memoryGroupsSyncedAt = 0;
    memoryBrands = null;
    memoryBrandsSyncedAt = 0;
    memoryUnits = null;
    memoryUnitsSyncedAt = 0;
    await clearAllErpCaches();
    emitStatus();
  }, []);

  useEffect(() => {
    const handleProductCreated = (event: {
      productId?: number;
      data?: ErpProduct;
    }) => {
      if (event?.data && event.data.id) {
        addProductToCache(event.data);
      } else {
        invalidateProducts();
      }
    };

    const handleProductUpdated = (event: {
      productId?: number;
      data?: ErpProduct;
    }) => {
      if (event?.data && (event.data.id || event.productId)) {
        const id = event.data.id || event.productId!;
        updateProductInCache(id, event.data);
      } else if (event?.productId) {
        void refreshProductInCache(event.productId);
      } else {
        invalidateProducts();
      }
    };

    const handleProductDeleted = (event: { productId?: number }) => {
      if (event?.productId) {
        removeProductFromCache(event.productId);
      } else {
        invalidateProducts();
      }
    };

    const handleStockMovement = (event: {
      productId?: number;
      data?: { stock_quantity?: number };
    }) => {
      if (event?.productId && event?.data?.stock_quantity != null) {
        updateProductStockInCache(event.productId, event.data.stock_quantity);
      } else if (event?.productId) {
        void refreshProductInCache(event.productId);
      } else {
        invalidateProducts();
      }
    };

    const handleStockUpdated = (event: {
      productId?: number;
      data?: { stock_quantity?: number };
    }) => {
      if (event?.productId && event?.data?.stock_quantity != null) {
        updateProductStockInCache(event.productId, event.data.stock_quantity);
      } else if (event?.productId) {
        void refreshProductInCache(event.productId);
      }
    };

    const handleGroupChanged = () => {
      invalidateProducts();
      invalidateGroups();
    };

    const handleCacheInvalidateAll = () => {
      void clearAllCaches();
    };

    productCacheEvents.on('product:created', handleProductCreated);
    productCacheEvents.on('product:updated', handleProductUpdated);
    productCacheEvents.on('product:deleted', handleProductDeleted);
    productCacheEvents.on('product:stock-movement', handleStockMovement);
    productCacheEvents.on('product:stock-updated', handleStockUpdated);
    productCacheEvents.on('product:group-changed', handleGroupChanged);
    productCacheEvents.on('cache:invalidate-all', handleCacheInvalidateAll);

    return () => {
      productCacheEvents.off('product:created', handleProductCreated);
      productCacheEvents.off('product:updated', handleProductUpdated);
      productCacheEvents.off('product:deleted', handleProductDeleted);
      productCacheEvents.off('product:stock-movement', handleStockMovement);
      productCacheEvents.off('product:stock-updated', handleStockUpdated);
      productCacheEvents.off('product:group-changed', handleGroupChanged);
      productCacheEvents.off('cache:invalidate-all', handleCacheInvalidateAll);
    };
  }, [
    addProductToCache,
    updateProductInCache,
    removeProductFromCache,
    updateProductStockInCache,
    refreshProductInCache,
    invalidateProducts,
    invalidateGroups,
    clearAllCaches,
  ]);

  return {
    getProducts,
    getGroups,
    getBrands,
    getUnits,
    searchProductsLocal,
    updateProductInCache,
    addProductToCache,
    removeProductFromCache,
    updateProductStockInCache,
    invalidateProducts,
    invalidateGroups,
    invalidateBrands,
    invalidateUnits,
    clearAllCaches,
    /** @deprecated use clearAllCaches */
    clearCache: (key?: string) => {
      if (!key || key.includes('product')) invalidateProducts();
      else if (key.includes('group')) invalidateGroups();
      else if (key.includes('brand')) invalidateBrands();
      else if (key.includes('unit')) invalidateUnits();
    },
    loading,
    revalidating,
    error,
    isOffline,
    isStale,
    lastSyncedAt,
  };
};
